import React from 'react';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';

const LoadingPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden',
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
  boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.18)}, 0 2px 8px ${alpha(theme.palette.common.black, 0.06)}`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    opacity: 0.9,
  },
}));

/**
 * Modern Professional Loading Spinner Component
 * @param {Object} props
 * @param {string} props.message - Loading message
 * @param {string} props.size - Size of spinner (small, medium, large)
 * @param {boolean} props.fullScreen - Whether to show full screen
 */
const LoadingSpinner = ({ message = "Loading...", size = "medium", fullScreen = false }) => {
  const theme = useTheme();
  const sizeMap = {
    small: 24,
    medium: 40,
    large: 56,
  };

  const spinner = (
    <LoadingPaper elevation={0}>
      <CircularProgress
        size={sizeMap[size]}
        thickness={3.6}
        sx={{
          color: theme.palette.primary.main,
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
          },
        }}
      />
      {message && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
            textAlign: 'center',
          }}
        >
          {message}
        </Typography>
      )}
    </LoadingPaper>
  );

  if (fullScreen) {
    return (
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
          zIndex: 9999,
        }}
      >
        {spinner}
      </Box>
    );
  }

  return spinner;
};

export default LoadingSpinner;